import { useEffect, useRef, useState, type ReactNode } from "react";
import { useUser } from "~/hooks/use-user";
import { CheckoutSessionProvider, useCheckoutSession } from "./CheckoutSession";
import { useNavigate, useRouteLoaderData, useSearchParams } from "react-router";
import type { loader as confirmationLoader } from "~/routes/plans.$eventId.purchase-confirmation/route";
import type { loader as processingLoader } from "~/routes/plans.$eventId.purchase-processing/route";
import type { loader as followUpLoader } from "~/routes/plans.$eventId.your-going/route";
import type { EventFragment } from "~/__generated__/graphql/graphql";
import { PlanDialog } from "~/components/plans/components/PlanDialog";
import { track } from "~/utils/analytics";
import { useRootEnvironment } from "~/root";
import { checkoutContactEmailSchema } from "~/utils/contact-email";
import { ApprovalCheckoutDialog } from "./ApprovalCheckoutDialog";
import { CheckoutDetailsStep, checkoutNameSchema } from "./CheckoutDetailsStep";
import { CheckoutEmailStep } from "./CheckoutEmailStep";
import { CheckoutPaymentStep } from "./CheckoutPaymentStep";
import { CheckoutTicketsStep } from "./CheckoutTicketsStep";
import { TICKET_VARIATION_PARAM } from "./ticketVariations";
import { isTicketCheckoutAvailable } from "./ticketSalesAvailability";

export type CheckoutStep = "tickets" | "details" | "email" | "payment";

const VALID_STEPS = new Set<CheckoutStep>(["tickets", "details", "email", "payment"]);

export const CHECKOUT_STEP_PARAM = "step";

export function isCheckoutStep(value: string | null): value is CheckoutStep {
  return value !== null && VALID_STEPS.has(value as CheckoutStep);
}

type Props = {
  plan: EventFragment;
  authenticated: boolean;
  userExists: boolean;
  userAnonymous: boolean;
  stripePublishableKey: string | undefined;
  ticketVariationsEnabled: boolean;
  onResumeChange?: (walletIntentID: string) => void;
};

export function CheckoutDialog(props: Props) {
  const { user } = useUser();
  return <CheckoutSessionProvider key={props.plan.id} eventId={props.plan.id} userId={user?.id}><CheckoutFlow {...props} /></CheckoutSessionProvider>;
}

function CheckoutFlow(props: Props) {
  const checkout = useCheckoutSession();
  const [search] = useSearchParams();
  const confirmation = useRouteLoaderData<typeof confirmationLoader>("routes/plans.$eventId.purchase-confirmation");
  const processing = useRouteLoaderData<typeof processingLoader>("routes/plans.$eventId.purchase-processing");
  const processingIntentID = processing && "walletIntentID" in processing ? processing.walletIntentID : null;
  const followUp = useRouteLoaderData<typeof followUpLoader>("routes/plans.$eventId.your-going");
  const followUpIntentID = followUp?.walletIntentID;
  const open = search.has("step");
  const { walletIntentID } = checkout.draft;
  const completed = !!walletIntentID && confirmation?.walletIntentID === walletIntentID;
  const { ready, complete, retainRecovery } = checkout;
  const onResumeChange = props.onResumeChange;
  useEffect(() => { onResumeChange?.(ready && !completed ? walletIntentID : ""); }, [ready, completed, walletIntentID, onResumeChange]);
  useEffect(() => {
    // Only the API-verified confirmation loader can retire this attempt. Attendance
    // alone may still be a reservation, and post-payment questions may be unfinished.
    if (ready && completed) complete(walletIntentID);
  }, [ready, completed, walletIntentID, complete]);
  useEffect(() => {
    if (ready && processingIntentID) retainRecovery(processingIntentID);
  }, [ready, processingIntentID, retainRecovery]);
  useEffect(() => {
    if (ready && followUpIntentID) retainRecovery(followUpIntentID, true);
  }, [ready, followUpIntentID, retainRecovery]);
  if (!open) return null;
  if (!ready) return <PlanDialog onClose={checkout.close} fullScreenOnMobile><p role="status" className="font-sans text-sm text-butter-green">{checkout.error ?? "Restoring checkout…"}</p></PlanDialog>;
  return (
    <CheckoutRecovery key={checkout.scope} eventId={props.plan.id}>
      {props.plan.approveGuests ? (
        <ApprovalCheckoutDialog
          plan={props.plan}
          authenticated={props.authenticated}
          userExists={props.userExists}
          stripePublishableKey={props.stripePublishableKey}
          ticketVariationsEnabled={props.ticketVariationsEnabled}
        />
      ) : <InstantCheckoutDialog {...props} />}
    </CheckoutRecovery>
  );
}

function CheckoutRecovery({ eventId, children }: { eventId: string; children: ReactNode }) {
  const checkout = useCheckoutSession();
  const navigate = useNavigate();
  // Check only the attempt present when checkout opens, not an attempt created by
  // the live payment form. Remounting Stripe fields mid-checkout would lose input.
  const [{ walletIntentID, paymentPhase }] = useState(checkout.draft);
  const [checking, setChecking] = useState(!!walletIntentID);
  const { canContinue, complete, update } = checkout;
  useEffect(() => {
    if (!walletIntentID) return;

    const resume = () => {
      if (paymentPhase === "processing" && canContinue()) {
        void navigate(`/plans/${eventId}/purchase-processing?${new URLSearchParams({ walletIntentID })}`, { replace: true });
      } else {
        setChecking(false);
      }
    };
    const controller = new AbortController();
    const timeout = window.setTimeout(() => { controller.abort(); resume(); }, 10000);
    void fetch(`/api/events/${eventId}/checkout-status?${new URLSearchParams({ walletIntentID })}`, { signal: controller.signal })
      .then(response => response.ok ? response.json() : null)
      .then(result => {
        if (controller.signal.aborted) return;
        if (result?.destination && canContinue()) {
          if (result.kind === "request" && result.walletIntentID === walletIntentID) {
            complete(walletIntentID);
            setChecking(false);
          }
          void navigate(result.destination, { replace: true });
        } else if (result?.kind === "missing" && canContinue()) {
          complete(walletIntentID);
          setChecking(false);
        } else if (result?.kind === "failed" && result.walletIntentID === walletIntentID && canContinue()) {
          update({ walletIntentID: "", paymentPhase: "overview" });
          setChecking(false);
        } else {
          resume();
        }
      })
      .catch(() => {
        // A failed read is not a failed payment. The purchase command can still
        // recover the same attempt, without a fresh card confirmation.
        if (!controller.signal.aborted) resume();
      })
      .finally(() => window.clearTimeout(timeout));
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [walletIntentID, paymentPhase, eventId, canContinue, complete, update, navigate]);
  if (!checking) return children;
  return (
    <PlanDialog onClose={checkout.close} fullScreenOnMobile>
      <p role="status" className="font-sans text-sm text-butter-green">Checking your ticket…</p>
    </PlanDialog>
  );
}

function InstantCheckoutDialog({
  plan,
  authenticated,
  userExists,
  stripePublishableKey,
  ticketVariationsEnabled,
}: Props) {
  const checkout = useCheckoutSession();
  const { user } = useUser();
  const ticketOutcomeSMSEnabled = useRootEnvironment()?.TICKET_OUTCOME_SMS_ENABLED;
  // Existing attempts must remain resumable when the delivery flag changes.
  const needsContactEmail =
    !ticketOutcomeSMSEnabled &&
    !checkout.draft.walletIntentID &&
    !plan.isAttending &&
    !checkoutContactEmailSchema.safeParse(user?.contactEmail).success;
  const hasDetails =
    authenticated &&
    userExists &&
    !!user?.phoneNumber?.trim() &&
    checkoutNameSchema.safeParse(user?.name).success;
  const nextStep = !hasDetails ? "details" : needsContactEmail ? "email" : "payment";
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get(CHECKOUT_STEP_PARAM);
  const step = isCheckoutStep(stepParam) ? stepParam : null;
  const checkoutAvailable = isTicketCheckoutAvailable(plan);
  const open = step !== null && checkoutAvailable;

  const checkoutWasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !checkoutWasOpenRef.current) {
      track("checkout_started", {
        event_id: plan.id,
        event_title: plan.title,
      });
    }
    checkoutWasOpenRef.current = open;
  }, [open, plan.id, plan.title]);

  useEffect(() => {
    if (step === null || checkoutAvailable) return;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(CHECKOUT_STEP_PARAM);
        next.delete("discountCode");
        next.delete(TICKET_VARIATION_PARAM);
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  }, [checkoutAvailable, setSearchParams, step]);

  const lastStepRef = useRef<CheckoutStep | null>(step);
  if (open) lastStepRef.current = step;
  const requestedDisplayedStep = step ?? lastStepRef.current;
  const displayedStep =
    requestedDisplayedStep === "payment" || requestedDisplayedStep === "email"
      ? nextStep
      : requestedDisplayedStep;

  const close = () => {
    if (step !== null) {
      track("checkout_abandoned", {
        event_id: plan.id,
        event_title: plan.title,
        step,
      });
    }
    checkout.close();
  };

  return (
    <PlanDialog open={open} onClose={close} fullScreenOnMobile>
      {checkout.error && <p role="alert" className="mb-3 font-sans text-sm text-butter-pink">{checkout.error} <button type="button" className="underline" onClick={() => void checkout.flush()}>Try again</button></p>}
      {displayedStep === "tickets" && (
        <CheckoutTicketsStep
          plan={plan}
          ticketVariationsEnabled={ticketVariationsEnabled}
          nextStep={nextStep}
        />
      )}
      {displayedStep === "details" && (
        <CheckoutDetailsStep
          plan={plan}
          authenticated={authenticated}
          userExists={userExists}
        />
      )}
      {displayedStep === "email" && (
        <CheckoutEmailStep
          eventId={plan.id}
          onBack={() => checkout.setStep("tickets")}
        />
      )}
      {displayedStep === "payment" && (
        <CheckoutPaymentStep
          onBack={() => checkout.setStep("tickets")}
          plan={plan}
          authenticated={authenticated}
          userExists={userExists}
          stripePublishableKey={stripePublishableKey}
          ticketVariationsEnabled={ticketVariationsEnabled}
        />
      )}
    </PlanDialog>
  );
}
